import uslug from 'uslug';

const WHITESPACE_REGEX = /\p{White_Space}/gu;

const WHITESPACE_WITHOUT_NEWLINE_REGEX = /(?![\n\r\u2028\u2029])\p{White_Space}/gu;

const NEWLINE_REGEX = /[\n\r\u2028\u2029]/gu;

export function replaceNewline(str: string | undefined, replaceWith: string) {
  return str?.normalize().replace(NEWLINE_REGEX, replaceWith) || '';
}

export function replaceWhitespace(
  str: string | undefined,
  replaceWith: string,
  options?: { excludeNewline?: boolean }
): string {
  return (
    str
      ?.normalize()
      .replace(
        options?.excludeNewline ? WHITESPACE_WITHOUT_NEWLINE_REGEX : WHITESPACE_REGEX,
        replaceWith
      ) || ''
  );
}

export function collapseAllWhiteSpace(
  str: string,
  options?: { maxNewlines?: number; maxSpaces?: number }
): string {
  const { maxNewlines = 0, maxSpaces = 1 } = options || {};

  // Normalize all newlines
  let result = replaceNewline(str, '\n');

  // Normalize all spaces
  result = replaceWhitespace(result, ' ', { excludeNewline: true });

  // Remove spaces surrounding newlines
  result = result.replace(/\u0020*(\n+)\u0020*/gu, '$1');

  // Collapse newlines
  if (maxNewlines === 0) {
    result = result.replace(/\n+/gu, ' ');
  } else {
    const newlineRegex = new RegExp(`${repeatStr('\n', maxNewlines + 1)}+`, 'gu');
    result = result.replace(newlineRegex, repeatStr('\n', maxNewlines));
  }

  // Collapse spaces
  const spaceRegex = new RegExp(`${repeatStr(' ', maxSpaces + 1)}+`, 'gu');
  result = result.replace(spaceRegex, repeatStr(' ', maxSpaces)).trim();

  return result;
}

export function slugifyString(str = ''): string {
  return uslug(str, { allowedChars: '-_~' });
}

export function isEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim() === '';
}

export function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim() !== '';
}

export function splitNewline(str: string): string[] {
  return str.split(NEWLINE_REGEX).filter(s => !!s.trim());
}

export function repeatStr(str: string, num: number): string {
  return Array.from(Array(num + 1)).join(str);
}

export function clampText(str: string, maxLength?: number): string {
  const result = str.slice(0, maxLength);
  return maxLength && str.length > maxLength ? result + '…' : result;
}

export function toStringOrNullish(str: string | null | undefined): string | null | undefined {
  if (typeof str === 'undefined') {
    return;
  }

  if (str === null) {
    return null;
  }

  const result = str.normalize().trim();
  return result || null;
}

export function assertString(value: unknown): asserts value is string {
  if (typeof value !== 'string') {
    throw new Error('Invalid string');
  }
}

export function assertStringOrNull(value: unknown): asserts value is string | null {
  if (typeof value !== 'string' && value !== null) {
    throw new Error('Invalid string');
  }
}
